import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
    Box,
    TextField,
    IconButton,
    InputAdornment,
    Button,
    Chip,
    Stack,
} from "@mui/material";
import {
    Search as SearchIcon,
    Clear as ClearIcon,
    FilterList as FilterListIcon,
} from "@mui/icons-material";
import { OrderSearchParams } from "../../types";

interface OrderSearchBarProps {
    onSearch: (params: OrderSearchParams) => void;
    onToggleAdvanced: () => void;
    searchParams: OrderSearchParams;
}

export const OrderSearchBar: React.FC<OrderSearchBarProps> = ({
    onSearch,
    onToggleAdvanced,
    searchParams,
}) => {
    const [searchQuery, setSearchQuery] = useState(searchParams.search_query || "");
    // The search text this box last sent to the page.
    const lastSentQuery = useRef(searchParams.search_query);

    // The page changed the search text itself (e.g. "All {name}'s Orders"
    // clears it) — show that. Our own (debounced) sends are skipped, so a
    // late echo can never overwrite what's being typed.
    useEffect(() => {
        if (searchParams.search_query !== lastSentQuery.current) {
            lastSentQuery.current = searchParams.search_query;
            setSearchQuery(searchParams.search_query || "");
        }
    }, [searchParams.search_query]);

    // Debounce search input
    const debounceTimeout = React.useRef<NodeJS.Timeout | null>(null);

    const handleSearchChange = useCallback(
        (value: string) => {
            setSearchQuery(value);

            // Clear existing timeout
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }

            // Set new timeout
            debounceTimeout.current = setTimeout(() => {
                lastSentQuery.current = value || undefined;
                onSearch({
                    ...searchParams,
                    search_query: value || undefined,
                    skip: 0, // Reset to first page on new search
                });
            }, 500);
        },
        [onSearch, searchParams]
    );

    const handleClearSearch = () => {
        setSearchQuery("");
        lastSentQuery.current = undefined;
        onSearch({
            ...searchParams,
            search_query: undefined,
            skip: 0,
        });
    };

    // Quick filter helpers
    const handleQuickFilter = (days: number) => {
        const today = new Date();
        const fromDate = new Date(today);
        fromDate.setDate(today.getDate() - days);
        
        onSearch({
            ...searchParams,
            order_date_from: fromDate.toISOString().split('T')[0],
            order_date_to: today.toISOString().split('T')[0],
            skip: 0,
        });
    };

    const activeFiltersCount = useMemo(() => {
        let count = 0;
        if (searchParams.status) count++;
        if (searchParams.customer_id) count++;
        if (searchParams.product_id) count++;
        if (searchParams.order_date_from || searchParams.order_date_to) count++;
        if (searchParams.shipped_date_from || searchParams.shipped_date_to) count++;
        if (searchParams.delivered_date_from || searchParams.delivered_date_to) count++;
        if (searchParams.total_amount_min !== undefined || searchParams.total_amount_max !== undefined) count++;
        return count;
    }, [searchParams]);

    return (
        <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by order number, customer name, email, phone, or address..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={handleClearSearch}
                                    edge="end"
                                >
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    variant={activeFiltersCount > 0 ? "contained" : "outlined"}
                    startIcon={<FilterListIcon />}
                    onClick={onToggleAdvanced}
                    sx={{ minWidth: 150 }}
                >
                    Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
                </Button>
            </Stack>

            {/* Quick Filter Chips */}
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Chip
                    label="Today"
                    size="small"
                    onClick={() => handleQuickFilter(0)}
                    variant="outlined"
                />
                <Chip
                    label="Last 7 Days"
                    size="small"
                    onClick={() => handleQuickFilter(7)}
                    variant="outlined"
                />
                <Chip
                    label="Last 30 Days"
                    size="small"
                    onClick={() => handleQuickFilter(30)}
                    variant="outlined"
                />
                <Chip
                    label="Last 90 Days"
                    size="small"
                    onClick={() => handleQuickFilter(90)}
                    variant="outlined"
                />
            </Stack>
        </Box>
    );
};
